import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { SiArtstation } from "react-icons/si";
import { FaFlag } from "react-icons/fa";
import { BiCodeAlt } from "react-icons/bi";
import { IoIosPaper } from "react-icons/io";
import { GiCheckMark } from "react-icons/gi";
import { MdKeyboardArrowDown } from "react-icons/md";
import ResumeTitle from "./ResumeTitle";
import {
  codingSkills,
  frameworkSkills,
  languageSkills,
  knowledgeList,
} from "../../data/skills";

const SkillBar = ({ name, level }) => (
  <div className="py-3 border-b-[1px] border-lineColor">
    <p className="text-base text-textColor -mb-1.5">{name}</p>
    <span className="w-full bg-trackColor h-1 inline-flex relative">
      <span
        className="h-full absolute top-0 left-0 bg-designColor"
        style={{ width: level }}
      ></span>
    </span>
  </div>
);

const SkillGroup = ({ skills }) => (
  <div className="py-4">
    {skills.map(({ name, level }) => (
      <SkillBar key={name} name={name} level={level} />
    ))}
  </div>
);

// Below "lgl" (1024px): Coding / Frameworks / Languages become a horizontal
// swipe strip of self-contained cards — the same "swipe to browse" pattern
// as Recent Projects, Publications, and Core Competencies — instead of a
// tap-to-switch tab bar. Knowledge stays its own dropdown below. At "lgl"
// and up: unchanged, the original four-column grid.
const skillGroups = [
  { id: "coding", label: "Coding", icon: <BiCodeAlt />, skills: codingSkills },
  { id: "frameworks", label: "Frameworks", icon: <SiArtstation />, skills: frameworkSkills },
  { id: "languages", label: "Languages", icon: <FaFlag />, skills: languageSkills },
];

const Skills = () => {
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);

  return (
    <>
      <div className="w-full min-w-0 flex flex-col gap-4 pt-4 lgl:hidden">
        <p className="lgl:hidden text-xs text-faintColor px-7 -mt-1">
          Swipe to explore →
        </p>
        <div className="w-full min-w-0 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-1 px-7 scrollbar-thin scrollbar-thumb-scrollThumb">
          {skillGroups.map(({ id, label, icon, skills }) => (
            <div
              key={id}
              className="shrink-0 snap-start w-64 sm:w-72 rounded-2xl border border-lineColor bg-surfaceColor px-5 py-5"
            >
              <div className="flex items-center gap-2 text-designColor uppercase text-sm font-medium">
                <span className="text-lg">{icon}</span>
                {label}
              </div>
              <SkillGroup skills={skills} />
            </div>
          ))}
        </div>

        <div className="mx-7 borderAll rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setKnowledgeOpen((open) => !open)}
            aria-expanded={knowledgeOpen}
            className="w-full flex items-center justify-between gap-2 px-4 py-3.5 text-left bg-surfaceColor active:brightness-90 transition duration-150"
          >
            <span className="flex items-center gap-2 text-mutedColor uppercase text-sm font-medium">
              <span className="text-designColor text-xl">
                <IoIosPaper />
              </span>
              Knowledge
            </span>
            <MdKeyboardArrowDown
              className={`text-xl text-mutedColor duration-300 ${
                knowledgeOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          <AnimatePresence initial={false}>
            {knowledgeOpen && (
              <motion.div
                key="content"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="overflow-hidden"
              >
                <ul className="flex flex-col gap-3 px-4 pb-4 pt-1 border-t-[1px] border-lineColor">
                  {knowledgeList.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-textColor text-sm">
                      <span className="text-designColor text-base mt-0.5">
                        <GiCheckMark />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="w-full hidden lgl:grid grid-cols-9 gap-10 lgl:gap-0 px-6">
        <div className="col-span-9 md:col-span-4">
          <ResumeTitle title="Coding" icon={<BiCodeAlt />} />
          <SkillGroup skills={codingSkills} />
        </div>
        <div className="w-full h-full hidden lgl:flex justify-center items-center">
          <span className="w-[1px] h-full bg-lineColor inline-flex"></span>
        </div>

        <div className="col-span-9 md:col-span-4">
          <ResumeTitle title="Frameworks" icon={<SiArtstation />} />
          <SkillGroup skills={frameworkSkills} />
        </div>

        <div className="col-span-9 md:col-span-4">
          <ResumeTitle title="Languages" icon={<FaFlag />} />
          <SkillGroup skills={languageSkills} />
        </div>

        <div className="w-full h-full hidden lgl:flex justify-center items-center">
          <span className="w-[1px] h-full bg-lineColor inline-flex"></span>
        </div>
        <div className="col-span-9 md:col-span-4">
          <ResumeTitle title="Knowledge" icon={<IoIosPaper />} />
          <ul className="py-4 flex flex-col gap-3 border-b-[1px] border-b-lineColor">
            {knowledgeList.map((item) => (
              <li key={item} className="flex items-center gap-4 text-textColor">
                <span className="text-designColor text-lg">
                  <GiCheckMark />
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
};

export default Skills;
